/**
 * Words typed straight onto the room, the page or the offer: the words themselves take the caret, as the
 * guest reads them. Each keystroke goes where the field's own controls save it; leaving the field tidies it:
 * trimmed, put back when it was left empty, and offered to the bar's Undo when it changed.
 */
import { useLayoutEffect, useRef, type CSSProperties, type KeyboardEvent } from "react";
import { useToast } from "../../components/ui/Toast";

type Tag = "b" | "span" | "div";

interface EditableProps {
  as?: Tag;
  className?: string;
  style?: CSSProperties;
  /** The words as the board holds them now. */
  value: string;
  /** What the field is, for a screen reader: "The room’s name". */
  label: string;
  /** The hint on hover. */
  tip?: string;
  /** What an empty field says, drawn in its place until words are typed. */
  empty?: string;
  /** Not typeable now: while Before is held, the words are the ones from before. */
  off?: boolean;
  /** The most the field keeps. */
  max?: number;
  /** "A room needs a name." when it is the name; else "That can’t be empty." */
  name?: boolean;
  /** A code: upper case, no spaces, once typing ends. */
  code?: boolean;
  /** Every keystroke, as typed. */
  onType: (text: string) => void;
  /** Typing ended with a change: the words as kept, and as they were, for Undo. */
  onDone?: (text: string, was: string) => void;
}

/** The words in a field, as plain text on one line. */
function textOf(el: HTMLElement): string {
  return (el.textContent ?? "").replace(/\s*\n\s*/g, " ");
}

export function Editable({ as: As = "span", className, style, value, label, tip, empty, off, max, name, code, onType, onDone }: EditableProps) {
  const toast = useToast();
  const el = useRef<HTMLElement>(null);
  /** The words this field last said it holds: a value that differs came from somewhere else. */
  const sent = useRef<string | null>(null);
  /** The words when the caret came in. */
  const was = useRef<string | null>(null);

  // The field draws its own words: while it has the caret, only words from somewhere else replace them.
  useLayoutEffect(() => {
    const node = el.current;
    if (!node) return;
    const focused = document.activeElement === node;
    if (focused && value === sent.current) return;
    if (node.textContent !== value) node.textContent = value;
    if (focused) {
      // The board moved under the caret (another tab, a change taken back): the caret goes to the end.
      sent.current = value;
      window.getSelection()?.selectAllChildren(node);
      window.getSelection()?.collapseToEnd();
    }
  }, [value]);

  const leave = () => {
    const node = el.current;
    const before = was.current;
    was.current = null;
    if (!node || before === null) return;
    const typed = textOf(node).trim();
    sent.current = null;
    if (typed === before.trim()) {
      if (textOf(node) !== before) {
        node.textContent = before;
        onType(before);
      }
      return;
    }
    if (!typed) {
      node.textContent = before;
      onType(before);
      toast({ text: name ? "A room needs a name." : "That can’t be empty.", icon: "info" });
      return;
    }
    const kept = code ? typed.toUpperCase().replace(/\s+/g, "") : typed;
    node.textContent = kept;
    onType(kept);
    onDone?.(kept, before);
  };

  const keys = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.preventDefault();
      el.current?.blur();
    }
  };

  return (
    <As
      ref={el as never}
      className={className}
      style={style}
      title={off ? undefined : tip}
      data-empty={off ? undefined : empty}
      {...(off
        ? {}
        : {
            contentEditable: "plaintext-only" as const,
            suppressContentEditableWarning: true,
            spellCheck: false,
            role: "textbox",
            "aria-label": label,
            onFocus: () => {
              was.current = value;
              sent.current = value;
            },
            onInput: () => {
              const node = el.current;
              if (!node) return;
              let t = textOf(node);
              if (max && t.length > max) {
                t = t.slice(0, max);
                node.textContent = t;
                window.getSelection()?.selectAllChildren(node);
                window.getSelection()?.collapseToEnd();
              }
              sent.current = t;
              // Why: a field emptied while typing keeps its words on the board; leaving it empty puts them back.
              if (t.trim()) onType(t);
            },
            onBlur: leave,
            onKeyDown: keys,
          })}
    />
  );
}
